import { DateRange } from "react-day-picker"
import { isBefore } from "date-fns"
import { t } from "i18next"
import { z } from "zod"
import { stringRequired, textAreaDefaultMaxLength } from "@/models/common"
import {
  CertificateType,
  SickLeaveReason,
  WorkCapacityType,
} from "@/modules/patients/detail/sick-leave-certificate/ch/services/__generated__/useChCreateSickLeaveMutation.graphql"
import { Language } from "@/types/__generated__/union-types"

const CERTIFICATE_TYPE: Record<CertificateType, CertificateType> = {
  FIRST: "FIRST",
  FOLLOW_UP: "FOLLOW_UP",
} as const

const SICK_LEAVE_REASON_TYPE: Record<SickLeaveReason, SickLeaveReason> = {
  SICKNESS: "SICKNESS",
  ACCIDENT: "ACCIDENT",
} as const

const WORK_CAPACITY_TYPE: Record<WorkCapacityType, WorkCapacityType> = {
  EMPLOYMENT_RATE: "EMPLOYMENT_RATE",
  HOURS_PER_WEEK: "HOURS_PER_WEEK",
} as const

export const ChSickLeaveCertificateSchema = z
  .object({
    language: z.custom<Exclude<Language, "ENGLISH">>(),
    certificateType: z.custom<CertificateType>(),
    timeFrame: z.custom<DateRange>(),
    reason: z.custom<SickLeaveReason>(),
    workCapacity: z
      .number()
      .nonnegative({ message: t("common.fieldErrorList.valueInRange", { min: 0, max: 99 }) })
      .nullable(),
    capacityType: z.custom<WorkCapacityType>(),
    employerName: stringRequired.max(200, { message: t("common.fieldErrorList.characterLimit") }),
    additionalInformation: z
      .string()
      .max(textAreaDefaultMaxLength, { message: t("common.fieldErrorList.characterLimit") })
      .nullable(),
    followUpInfo: z.object({
      id: z.string().nullable(),
      to: z.string().nullable(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.capacityType === WORK_CAPACITY_TYPE.EMPLOYMENT_RATE && data.workCapacity && data.workCapacity > 99) {
      ctx.addIssue({
        code: "custom",
        path: ["workCapacity"],
        message: t("common.fieldErrorList.valueInRange", { min: 0, max: 99 }),
      })
    }
    const { from, to } = data.timeFrame
    if (
      !to ||
      (to && from && isBefore(to, from)) ||
      (data.certificateType !== "FIRST" &&
        data.followUpInfo.to &&
        from &&
        isBefore(from, new Date(data.followUpInfo.to)))
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["timeFrame"],
        message: "common.fieldErrorList.invalidDateRange",
      })
    }
  })

type ChSickLeaveCertificateType = z.infer<typeof ChSickLeaveCertificateSchema>

export { type ChSickLeaveCertificateType, CERTIFICATE_TYPE, SICK_LEAVE_REASON_TYPE, WORK_CAPACITY_TYPE }
